import { useEffect, useMemo } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2, Plus, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import { cn } from "@/utils/cn";
import { useDraftMeeting, useSaveDraftMeeting, draftCalendarKeys } from "@/services/draftCalendar";
import { useQueryClient } from "@tanstack/react-query";
import type { FiscalYearResponse } from "@/types/draftCalendar";
import { useMsal } from "@azure/msal-react";

// ─── Zod schema ────────────────────────────────────────────────────────────────

const preferredDateSchema = z.object({
  id: z.number().optional(),
  roomBlockStartDate: z.string().min(1, "Room block start is required"),
  roomBlockEndDate: z.string().min(1, "Room block end is required"),
  meetingStartDate: z.string().min(1, "Meeting start is required"),
  meetingEndDate: z.string().min(1, "Meeting end is required"),
  allInAttendees: z.number().min(0, "Must be ≥ 0"),
  isSelected: z.boolean(),
  isItPrimaryDate: z.boolean(),
  isDeleted: z.boolean()
}).refine((d) => d.roomBlockEndDate >= d.roomBlockStartDate, {
  message: "Room block end must be on or after start",
  path: ["roomBlockEndDate"]
}).refine((d) => d.meetingEndDate >= d.meetingStartDate, {
  message: "Meeting end must be on or after start",
  path: ["meetingEndDate"]
});

const addMeetingSchema = z.object({
  meetingName: z.string().min(1, "Meeting name is required"),
  business: z.string().min(1, "Business is required"),
  meetingType: z.string().min(1, "Meeting type is required"),
  businessLine: z.string().min(1, "Business line is required"),
  meetingCategory: z.string().min(1, "Meeting category is required"),
  state: z.string().min(1, "State is required"),
  /** Core | Secondary */
  status: z.string().min(1, "Tier is required"),
  showInGRC: z.boolean(),
  location: z.string().optional(),
  attendeeLevel: z.string().optional(),
  programOwner: z.string().optional(),
  businessAdvisor: z.string().optional(),
  notes: z.string().optional(),
  /** SignOff status (optional, permission-based) */
  signOffStatus: z.string().optional(),
  /** Prior ID (optional, not for business approvers) */
  priorId: z.number().optional(),
  /** DU Decline Reason (conditional on state === "DU Declined") */
  duDeclineReason: z.string().optional(),
  /** DU Decline Comments (conditional on duDeclineReason === "Other") */
  duDeclineComments: z.string().optional(),
  draftPreferredDates: z.array(preferredDateSchema).min(1, "At least one preferred date is required")
});

type AddMeetingFormValues = z.infer<typeof addMeetingSchema>;

// ─── Props ─────────────────────────────────────────────────────────────────────

interface AddMeetingFormProps {
  /** 0 = new meeting, > 0 = edit */
  meetingId: number;
  fiscalYear: FiscalYearResponse | undefined;
  businesses: Record<string, string[]>;
  meetingTypes: Record<string, string[]>;
  attendeeLevels: string[];
  locations: string[];
  duDeclinedReasons?: string[];
  isViewOnly: boolean;
  isBusinessApprover?: boolean;
  canEditSignOffStatus?: boolean;
  onSaved: () => void;
  onCancel: () => void;
}

const STATES = [
  "New Request",
  "Business Review",
  "Business Approved",
  "DU Review",
  "Scheduled",
  "Resolved",
  "DU Declined",
  "Removed"
];
const TIERS = ["Core", "Secondary"];
const CATEGORIES = ["Internal", "External", "Client-Internal", "Client-External"];

/**
 * Add / Edit Draft Meeting form.
 * Mirrors Blazor CreateDraftEvent fields + preferred dates section.
 * Schema validation is Zod only – no ad-hoc JSX validation.
 */
export function AddMeetingForm({
  meetingId,
  fiscalYear,
  businesses,
  meetingTypes,
  attendeeLevels,
  locations,
  duDeclinedReasons = [],
  isViewOnly,
  isBusinessApprover = false,
  canEditSignOffStatus = false,
  onSaved,
  onCancel
}: AddMeetingFormProps) {
  // Guard every array/object prop against null.
  // Destructuring defaults (= []) only fire for undefined; APIs can return null.
  const safeAttendeeLevels: string[] = attendeeLevels ?? [];
  const safeLocations: string[] = locations ?? [];
  const safeBusinesses: Record<string, string[]> = businesses ?? {};
  const safeMeetingTypes: Record<string, string[]> = meetingTypes ?? {};
  const safeDuDeclinedReasons: string[] = duDeclinedReasons ?? [];

  const { accounts } = useMsal();
  const userEmail = accounts[0]?.username ?? "";
  const queryClient = useQueryClient();
  const isEdit = meetingId > 0;

  // Load existing meeting if editing
  const { data: existing, isPending: loadingMeeting } = useDraftMeeting(meetingId);
  const saveMutation = useSaveDraftMeeting();

  const form = useForm<AddMeetingFormValues>({
    resolver: zodResolver(addMeetingSchema),
    defaultValues: {
      meetingName: "",
      business: "",
      meetingType: "",
      businessLine: "",
      meetingCategory: "",
      state: "Draft",
      status: "Core",
      showInGRC: false,
      location: "",
      attendeeLevel: "",
      programOwner: "",
      businessAdvisor: "",
      notes: "",
      signOffStatus: "",
      priorId: undefined,
      duDeclineReason: "",
      duDeclineComments: "",
      draftPreferredDates: [defaultPreferredDate()]
    }
  });

  const { fields: dateFields, append: appendDate, remove: removeDate } =
    useFieldArray({ control: form.control, name: "draftPreferredDates" });

  const watchedBusiness = form.watch("business");
  const watchedState = form.watch("state");
  const watchedDuDeclineReason = form.watch("duDeclineReason");

  // Business lines scoped to the selected business
  const availableLines = useMemo(
    () => safeBusinesses[watchedBusiness] ?? [],
    [safeBusinesses, watchedBusiness]
  );

  // Meeting types scoped to the selected business
  const availableMeetingTypes = useMemo(
    () => safeMeetingTypes[watchedBusiness] ?? Object.values(safeMeetingTypes).flat(),
    [safeMeetingTypes, watchedBusiness]
  );

  // Pre-fill form when editing
  useEffect(() => {
    if (isEdit && existing) {
      form.reset({
        meetingName: existing.meetingName ?? "",
        business: existing.business ?? "",
        meetingType: existing.meetingType ?? "",
        businessLine: existing.businessLine ?? "",
        meetingCategory: existing.meetingCategory ?? "",
        state: existing.state ?? "Draft",
        status: existing.status ?? "Core",
        showInGRC: existing.showInGRC ?? false,
        location: existing.location ?? "",
        attendeeLevel: existing.attendeeLevel ?? "",
        programOwner: existing.programOwner ?? "",
        businessAdvisor: existing.businessAdvisor ?? "",
        notes: existing.notes ?? "",
        signOffStatus: existing.signOffStatus ?? "",
        priorId: existing.priorID ?? undefined,
        duDeclineReason: existing.duDeclineReason ?? "",
        duDeclineComments: existing.duDeclineComments ?? "",
        draftPreferredDates:
          existing.draftPreferredDates.length > 0
            ? existing.draftPreferredDates.map((d) => ({
                id: d.id,
                roomBlockStartDate: d.roomBlockStartDate ?? "",
                roomBlockEndDate: d.roomBlockEndDate ?? "",
                meetingStartDate: d.meetingStartDate ?? "",
                meetingEndDate: d.meetingEndDate ?? "",
                allInAttendees: d.allInAttendees,
                isSelected: d.isSelected,
                isItPrimaryDate: d.isItPrimaryDate,
                isDeleted: false
              }))
            : [defaultPreferredDate()]
      });
    }
  }, [isEdit, existing, form]);

  async function onSubmit(values: AddMeetingFormValues) {
    try {
      await saveMutation.mutateAsync({
        ...(isEdit ? { id: meetingId } : {}),
        state: values.state,
        showInGRC: values.showInGRC,
        status: values.status,
        business: values.business,
        meetingName: values.meetingName,
        location: values.location || null,
        meetingType: values.meetingType,
        businessLine: values.businessLine,
        attendeeLevel: values.attendeeLevel || null,
        meetingCategory: values.meetingCategory,
        programOwner: values.programOwner || null,
        businessAdvisor: values.businessAdvisor || null,
        notes: values.notes || null,
        signOffStatus: values.signOffStatus || null,
        priorID: values.priorId || null,
        duDeclineReason: values.duDeclineReason || null,
        duDeclineComments: values.duDeclineComments || null,
        updatedByEmail: userEmail,
        userId: 0, // resolved server-side from email
        fiscalYear: fiscalYear?.id,
        programManagerName: null,
        programManagerEmail: null,
        draftPreferredDates: values.draftPreferredDates
      });

      await queryClient.invalidateQueries({ queryKey: draftCalendarKeys.all() });
      toast.success(isEdit ? "Meeting updated successfully." : "Meeting created successfully.");
      onSaved();
    } catch {
      toast.error("Unable to save meeting. Please try again.");
    }
  }

  const disabled = isViewOnly || saveMutation.isPending;

  if (isEdit && loadingMeeting) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <form
      id="meeting-form"
      onSubmit={form.handleSubmit(onSubmit)}
      noValidate
      className="flex flex-1 flex-col overflow-y-auto px-4 py-4 space-y-4"
    >
      {/* SignOff Status (permission-based visibility) */}
      {canEditSignOffStatus && (
        <Field label="Sign-Off Status" htmlFor="signoff-status">
          <select
            {...form.register("signOffStatus")}
            id="signoff-status"
            disabled={disabled || !canEditSignOffStatus}
            className={selectCls(false, disabled || !canEditSignOffStatus)}
          >
            <option value="">Select status</option>
            <option value="Pending">Pending</option>
            <option value="Signed Off">Signed Off</option>
            <option value="Rejected">Rejected</option>
          </select>
        </Field>
      )}

      {/* Meeting Name */}
      <Field label="Meeting Name" htmlFor="meeting-name" required error={form.formState.errors.meetingName?.message}>
        <input
          {...form.register("meetingName")}
          id="meeting-name"
          type="text"
          readOnly={disabled}
          className={inputCls(!!form.formState.errors.meetingName, disabled)}
          aria-required="true"
        />
      </Field>

      {/* Business */}
      <Field label="Business" htmlFor="business" required error={form.formState.errors.business?.message}>
        <select
          {...form.register("business", {
            onChange: () => {
              form.setValue("meetingType", "");
              form.setValue("businessLine", "");
            }
          })}
          id="business"
          disabled={disabled}
          className={selectCls(!!form.formState.errors.business, disabled)}
          aria-required="true"
        >
          <option value="">Select business</option>
          {Object.keys(safeBusinesses).map((b) => (
            <option key={b} value={b}>{b}</option>
          ))}
        </select>
      </Field>

      {/* Prior ID & New ID (not for business approvers) */}
      {!isBusinessApprover && (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Prior ID" htmlFor="prior-id">
            <input
              {...form.register("priorId", { valueAsNumber: true })}
              id="prior-id"
              type="number"
              min={0}
              readOnly={disabled}
              className={inputCls(false, disabled)}
            />
          </Field>
          <Field label="New ID" htmlFor="new-id">
            <input
              id="new-id"
              type="text"
              readOnly={true}
              disabled={true}
              className={inputCls(false, true)}
              placeholder="Auto-generated"
            />
          </Field>
        </div>
      )}

      {/* Meeting Type */}
      <Field label="Meeting Type" htmlFor="meeting-type" required error={form.formState.errors.meetingType?.message}>
        <select
          {...form.register("meetingType")}
          id="meeting-type"
          disabled={disabled}
          className={selectCls(!!form.formState.errors.meetingType, disabled)}
          aria-required="true"
        >
          <option value="">Select meeting type</option>
          {availableMeetingTypes.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </Field>

      {/* Business Line */}
      <Field label="Business Line" htmlFor="business-line" required error={form.formState.errors.businessLine?.message}>
        <select
          {...form.register("businessLine")}
          id="business-line"
          disabled={disabled}
          className={selectCls(!!form.formState.errors.businessLine, disabled)}
          aria-required="true"
        >
          <option value="">Select business line</option>
          {availableLines.map((l) => (
            <option key={l} value={l}>{l}</option>
          ))}
        </select>
      </Field>

      {/* Meeting Category */}
      <Field label="Meeting Category" htmlFor="meeting-category" required error={form.formState.errors.meetingCategory?.message}>
        <select
          {...form.register("meetingCategory")}
          id="meeting-category"
          disabled={disabled}
          className={selectCls(!!form.formState.errors.meetingCategory, disabled)}
          aria-required="true"
        >
          <option value="">Select category</option>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </Field>

      {/* Draft Meeting Status */}
      <Field label="Draft Meeting Status" htmlFor="meeting-state" required error={form.formState.errors.state?.message}>
        <select
          {...form.register("state")}
          id="meeting-state"
          disabled={disabled}
          className={selectCls(!!form.formState.errors.state, disabled)}
          aria-required="true"
        >
          <option value="">Select status</option>
          {STATES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </Field>

      {/* DU Decline Reason (conditional: only when state === "DU Declined") */}
      {watchedState === "DU Declined" && (
        <Field label="Reason for DU Decline" htmlFor="du-decline-reason">
          <select
            {...form.register("duDeclineReason")}
            id="du-decline-reason"
            disabled={disabled}
            className={selectCls(false, disabled)}
          >
            <option value="">Select reason</option>
            {safeDuDeclinedReasons.map((reason) => (
              <option key={reason} value={reason}>{reason}</option>
            ))}
          </select>
        </Field>
      )}

      {/* DU Decline Comments (conditional: only when duDeclineReason === "Other") */}
      {watchedDuDeclineReason === "Other" && (
        <Field label="DU Decline Comment" htmlFor="du-decline-comments">
          <textarea
            {...form.register("duDeclineComments")}
            id="du-decline-comments"
            rows={2}
            readOnly={disabled}
            placeholder="Please provide additional details"
            className={cn(
              "w-full border border-slate-300 px-3 py-2 text-sm",
              disabled && "bg-slate-50 text-slate-500"
            )}
          />
        </Field>
      )}

      {/* Tier (not shown to business approvers) */}
      {!isBusinessApprover && (
        <Field label="Tier" htmlFor="meeting-tier" required error={form.formState.errors.status?.message}>
          <select
            {...form.register("status")}
            id="meeting-tier"
            disabled={disabled}
            className={selectCls(!!form.formState.errors.status, disabled)}
            aria-required="true"
          >
            {TIERS.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </Field>
      )}

      {/* Attendee Level */}
      <Field label="Attendee Level" htmlFor="attendee-level">
        <select
          {...form.register("attendeeLevel")}
          id="attendee-level"
          disabled={disabled}
          className={selectCls(false, disabled)}
        >
          <option value="">Select attendee level</option>
          <option value="1">select all</option>
          
        </select>
      </Field>

      {/* Location */}
      <Field label="Location" htmlFor="location">
        <select
          {...form.register("location")}
          id="location"
          disabled={disabled}
          className={selectCls(false, disabled)}
        >
          <option value="">Select location</option>
          {safeLocations.map((l) => (
            <option key={l} value={l}>{l}</option>
          ))}
        </select>
      </Field>

      {/* Program Owner */}
      <Field label="CLO / Sponsoring PPMD" htmlFor="program-owner">
        <input
          {...form.register("programOwner")}
          id="program-owner"
          type="text"
          readOnly={disabled}
          className={inputCls(false, disabled)}
          placeholder="Name"
        />
      </Field>

      {/* Business Advisor */}
      <Field label="Business Advisor" htmlFor="business-advisor">
        <input
          {...form.register("businessAdvisor")}
          id="business-advisor"
          type="text"
          readOnly={disabled}
          className={inputCls(false, disabled)}
          placeholder="Name"
        />
      </Field>

      {/* Show In GRC */}
      <div className="flex items-center gap-2">
        <Controller
          control={form.control}
          name="showInGRC"
          render={({ field }) => (
            <input
              id="show-in-grc"
              type="checkbox"
              checked={field.value}
              onChange={field.onChange}
              disabled={disabled}
              className="h-4 w-4 rounded border-slate-300"
            />
          )}
        />
        <label htmlFor="show-in-grc" className="text-sm text-slate-700">Show in GRC</label>
      </div>

      {/* Notes */}
      <Field label="Notes" htmlFor="notes">
        <textarea
          {...form.register("notes")}
          id="notes"
          rows={3}
          readOnly={disabled}
          className={cn(
            "w-full border border-slate-300 px-3 py-2 text-sm",
            disabled && "bg-slate-50 text-slate-500"
          )}
        />
      </Field>

      {/* ── Preferred Dates ── */}
      <section aria-labelledby="preferred-dates-heading">
        <div className="mb-2 flex items-center justify-between">
          <h3 id="preferred-dates-heading" className="text-sm font-semibold text-slate-900">
            Preferred Dates
          </h3>
          {!disabled && (
            <button
              type="button"
              onClick={() => appendDate(defaultPreferredDate())}
              className="flex items-center gap-1 text-xs font-medium text-slate-700 hover:text-slate-900"
            >
              <Plus className="h-3.5 w-3.5" /> Add date
            </button>
          )}
        </div>
        {form.formState.errors.draftPreferredDates?.root && (
          <p className="mb-2 text-xs text-red-600">
            {form.formState.errors.draftPreferredDates.root.message}
          </p>
        )}

        <div className="space-y-4">
          {dateFields.map((field, idx) => (
            <PreferredDateBlock
              key={field.id}
              index={idx}
              form={form}
              disabled={disabled}
              canRemove={dateFields.length > 1}
              onRemove={() => removeDate(idx)}
              onSetPrimary={() => {
                dateFields.forEach((_, i) => {
                  form.setValue(`draftPreferredDates.${i}.isItPrimaryDate`, i === idx);
                });
              }}
            />
          ))}
        </div>
      </section>
    </form>
  );
}

// ── Preferred date block ────────────────────────────────────────────────────────

interface PreferredDateBlockProps {
  index: number;
  form: ReturnType<typeof useForm<AddMeetingFormValues>>;
  disabled: boolean;
  canRemove: boolean;
  onRemove: () => void;
  onSetPrimary: () => void;
}

function PreferredDateBlock({ index, form, disabled, canRemove, onRemove, onSetPrimary }: PreferredDateBlockProps) {
  const isPrimary = form.watch(`draftPreferredDates.${index}.isItPrimaryDate`);
  const errs = form.formState.errors.draftPreferredDates?.[index];
  const blockId = `date-block-${index}`;

  return (
    <fieldset className="rounded border border-slate-200 p-3">
      <legend className="px-1 text-xs font-medium text-slate-600">
        Date {index + 1}
        {isPrimary && <span className="ml-2 text-green-700">(Primary)</span>}
      </legend>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor={`${blockId}-rb-start`} className="mb-1 block text-xs font-medium text-slate-700">
            Room Block Start <span aria-hidden="true" className="text-red-500">*</span>
          </label>
          <input
            id={`${blockId}-rb-start`}
            type="date"
            {...form.register(`draftPreferredDates.${index}.roomBlockStartDate`)}
            readOnly={disabled}
            className={inputCls(!!errs?.roomBlockStartDate, disabled)}
          />
          {errs?.roomBlockStartDate && (
            <p className="mt-0.5 text-xs text-red-600">{errs.roomBlockStartDate.message}</p>
          )}
        </div>
        <div>
          <label htmlFor={`${blockId}-rb-end`} className="mb-1 block text-xs font-medium text-slate-700">
            Room Block End <span aria-hidden="true" className="text-red-500">*</span>
          </label>
          <input
            id={`${blockId}-rb-end`}
            type="date"
            {...form.register(`draftPreferredDates.${index}.roomBlockEndDate`)}
            readOnly={disabled}
            className={inputCls(!!errs?.roomBlockEndDate, disabled)}
          />
          {errs?.roomBlockEndDate && (
            <p className="mt-0.5 text-xs text-red-600">{errs.roomBlockEndDate.message}</p>
          )}
        </div>
        <div>
          <label htmlFor={`${blockId}-mtg-start`} className="mb-1 block text-xs font-medium text-slate-700">
            Meeting Start <span aria-hidden="true" className="text-red-500">*</span>
          </label>
          <input
            id={`${blockId}-mtg-start`}
            type="date"
            {...form.register(`draftPreferredDates.${index}.meetingStartDate`)}
            readOnly={disabled}
            className={inputCls(!!errs?.meetingStartDate, disabled)}
          />
          {errs?.meetingStartDate && (
            <p className="mt-0.5 text-xs text-red-600">{errs.meetingStartDate.message}</p>
          )}
        </div>
        <div>
          <label htmlFor={`${blockId}-mtg-end`} className="mb-1 block text-xs font-medium text-slate-700">
            Meeting End <span aria-hidden="true" className="text-red-500">*</span>
          </label>
          <input
            id={`${blockId}-mtg-end`}
            type="date"
            {...form.register(`draftPreferredDates.${index}.meetingEndDate`)}
            readOnly={disabled}
            className={inputCls(!!errs?.meetingEndDate, disabled)}
          />
          {errs?.meetingEndDate && (
            <p className="mt-0.5 text-xs text-red-600">{errs.meetingEndDate.message}</p>
          )}
        </div>
        <div>
          <label htmlFor={`${blockId}-attendees`} className="mb-1 block text-xs font-medium text-slate-700">
            All-In Attendees
          </label>
          <input
            id={`${blockId}-attendees`}
            type="number"
            min={0}
            {...form.register(`draftPreferredDates.${index}.allInAttendees`, { valueAsNumber: true })}
            readOnly={disabled}
            className={inputCls(false, disabled)}
          />
        </div>
      </div>

      {!disabled && (
        <div className="mt-3 flex items-center gap-4">
          {!isPrimary && (
            <button
              type="button"
              onClick={onSetPrimary}
              className="text-xs font-medium text-slate-600 underline hover:text-slate-900"
            >
              Set as primary
            </button>
          )}
          {canRemove && (
            <button
              type="button"
              onClick={onRemove}
              aria-label={`Remove date ${index + 1}`}
              className="ml-auto flex items-center gap-1 text-xs text-red-600 hover:text-red-800"
            >
              <Trash2 className="h-3.5 w-3.5" /> Remove
            </button>
          )}
        </div>
      )}
    </fieldset>
  );
}

// ── Utility components / helpers ────────────────────────────────────────────────

interface FieldProps {
  label: string;
  /** Must match the id prop on the child input/select. */
  htmlFor: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}

function Field({ label, htmlFor, required, error, children }: FieldProps) {
  return (
    <div>
      <label htmlFor={htmlFor} className="mb-1 block text-sm font-medium text-slate-800">
        {label}
        {required && <span aria-hidden="true" className="ml-0.5 text-red-500">*</span>}
      </label>
      {children}
      {error && <p role="alert" className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

function inputCls(hasError: boolean, disabled: boolean) {
  return cn(
    "w-full border px-3 py-2 text-sm",
    hasError ? "border-red-500" : "border-slate-300",
    disabled && "bg-slate-50 text-slate-500"
  );
}

function selectCls(hasError: boolean, disabled: boolean) {
  return cn(
    "w-full border px-3 py-2 text-sm",
    hasError ? "border-red-500" : "border-slate-300",
    disabled && "bg-slate-50 text-slate-500"
  );
}

function defaultPreferredDate() {
  return {
    roomBlockStartDate: "",
    roomBlockEndDate: "",
    meetingStartDate: "",
    meetingEndDate: "",
    allInAttendees: 0,
    isSelected: true,
    isItPrimaryDate: false,
    isDeleted: false
  };
}
